import { useState } from 'react';
import { toast } from 'sonner';
import { Upload, PenLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Formulario, FormularioCampo } from '@/types';

function opcoesDe(campo: FormularioCampo): string[] {
  const cfg = (campo.configuracao || {}) as Record<string, unknown>;
  return Array.isArray(cfg.opcoes) ? (cfg.opcoes as string[]) : [];
}

function escalaDe(campo: FormularioCampo): { min: number; max: number } {
  const cfg = (campo.configuracao || {}) as Record<string, unknown>;
  return {
    min: typeof cfg.min === 'number' ? cfg.min : 1,
    max: typeof cfg.max === 'number' ? cfg.max : 5,
  };
}

function permiteOutroDe(campo: FormularioCampo): boolean {
  const cfg = (campo.configuracao || {}) as Record<string, unknown>;
  return cfg.permiteOutro === true;
}

const OUTRO_VALOR = '__outro__';

// Datas/horas nativas (`<input type="date/time/datetime-local">`) já bloqueiam a maior parte
// dos formatos inválidos no próprio seletor do navegador, mas ainda dá pra digitar um valor que
// "não existe de verdade" (ex: 31/02) em alguns navegadores — daí essa checagem extra.
function erroDeData(tipo: 'data' | 'hora' | 'data_hora', valor: string): string | null {
  if (!valor) return null;
  if (tipo === 'hora') {
    const m = /^(\d{2}):(\d{2})$/.exec(valor);
    if (!m) return 'Horário em formato inválido.';
    const [, h, min] = m;
    if (Number(h) > 23 || Number(min) > 59) return 'Horário inexistente.';
    return null;
  }
  const dataParte = tipo === 'data' ? valor : valor.split('T')[0];
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dataParte);
  if (!m) return 'Data em formato inválido.';
  const [, ano, mes, dia] = m;
  const d = new Date(Number(ano), Number(mes) - 1, Number(dia));
  const valida = d.getFullYear() === Number(ano) && d.getMonth() === Number(mes) - 1 && d.getDate() === Number(dia);
  if (!valida) return 'Essa data não existe no calendário.';
  return null;
}

function CampoPreview({
  campo, valor, onChange,
}: {
  campo: FormularioCampo;
  valor: unknown;
  onChange: (v: unknown) => void;
}) {
  const [outroTexto, setOutroTexto] = useState('');

  const label = (
    <Label className="flex items-center gap-1">
      {campo.label}
      {campo.obrigatorio && <span className="text-destructive">*</span>}
    </Label>
  );

  switch (campo.tipo) {
    case 'texto_curto':
      return <div className="space-y-2">{label}<Input value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} /></div>;
    case 'texto_longo':
      return <div className="space-y-2">{label}<Textarea rows={3} className="text-left" value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} /></div>;
    case 'numero':
      return <div className="space-y-2">{label}<Input type="number" value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} /></div>;
    case 'data': {
      const erro = erroDeData('data', (valor as string) || '');
      return (
        <div className="space-y-2">
          {label}
          <Input type="date" value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} />
          {erro && <p className="text-sm text-muted-foreground">{erro}</p>}
        </div>
      );
    }
    case 'hora': {
      const erro = erroDeData('hora', (valor as string) || '');
      return (
        <div className="space-y-2">
          {label}
          <Input type="time" value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} />
          {erro && <p className="text-sm text-muted-foreground">{erro}</p>}
        </div>
      );
    }
    case 'data_hora': {
      const erro = erroDeData('data_hora', (valor as string) || '');
      return (
        <div className="space-y-2">
          {label}
          <Input type="datetime-local" value={(valor as string) || ''} onChange={(e) => onChange(e.target.value)} />
          {erro && <p className="text-sm text-muted-foreground">{erro}</p>}
        </div>
      );
    }
    case 'lista':
      return (
        <div className="space-y-2">
          {label}
          <Select value={(valor as string) || undefined} onValueChange={onChange}>
            <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
            <SelectContent>
              {opcoesDe(campo).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      );
    case 'selecao_unica': {
      const outroAtivo = permiteOutroDe(campo);
      return (
        <div className="space-y-2">
          {label}
          <RadioGroup value={(valor as string) || ''} onValueChange={onChange}>
            {opcoesDe(campo).map((o) => (
              <div key={o} className="flex items-center gap-2">
                <RadioGroupItem value={o} id={`${campo.id}-${o}`} />
                <Label htmlFor={`${campo.id}-${o}`} className="font-normal cursor-pointer">{o}</Label>
              </div>
            ))}
            {outroAtivo && (
              <div className="flex items-center gap-2">
                <RadioGroupItem value={OUTRO_VALOR} id={`${campo.id}-outro`} />
                <Label htmlFor={`${campo.id}-outro`} className="font-normal cursor-pointer">Outro</Label>
              </div>
            )}
          </RadioGroup>
          {outroAtivo && valor === OUTRO_VALOR && (
            <Input
              placeholder="Digite sua resposta..."
              value={outroTexto}
              onChange={(e) => setOutroTexto(e.target.value)}
              className="mt-1"
            />
          )}
        </div>
      );
    }
    case 'multipla_escolha': {
      const selecionadas = Array.isArray(valor) ? (valor as string[]) : [];
      const outroAtivo = permiteOutroDe(campo);
      return (
        <div className="space-y-2">
          {label}
          <div className="space-y-2">
            {opcoesDe(campo).map((o) => (
              <div key={o} className="flex items-center gap-2">
                <Checkbox
                  id={`${campo.id}-${o}`}
                  checked={selecionadas.includes(o)}
                  onCheckedChange={(checked) => {
                    onChange(checked ? [...selecionadas, o] : selecionadas.filter((s) => s !== o));
                  }}
                />
                <Label htmlFor={`${campo.id}-${o}`} className="font-normal cursor-pointer">{o}</Label>
              </div>
            ))}
            {outroAtivo && (
              <div className="flex items-center gap-2">
                <Checkbox
                  id={`${campo.id}-outro`}
                  checked={selecionadas.includes(OUTRO_VALOR)}
                  onCheckedChange={(checked) => {
                    onChange(checked ? [...selecionadas, OUTRO_VALOR] : selecionadas.filter((s) => s !== OUTRO_VALOR));
                  }}
                />
                <Label htmlFor={`${campo.id}-outro`} className="font-normal cursor-pointer">Outro</Label>
              </div>
            )}
          </div>
          {outroAtivo && selecionadas.includes(OUTRO_VALOR) && (
            <Input
              placeholder="Digite sua resposta..."
              value={outroTexto}
              onChange={(e) => setOutroTexto(e.target.value)}
              className="mt-1"
            />
          )}
        </div>
      );
    }
    case 'escala': {
      const { min, max } = escalaDe(campo);
      const opcoes = Array.from({ length: max - min + 1 }, (_, i) => min + i);
      return (
        <div className="space-y-2">
          {label}
          <div className="flex gap-2 flex-wrap">
            {opcoes.map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => onChange(n)}
                className={`h-9 w-9 rounded-full border text-sm font-medium ${valor === n ? 'bg-primary text-primary-foreground border-primary' : 'bg-background hover:bg-muted'}`}
              >
                {n}
              </button>
            ))}
          </div>
        </div>
      );
    }
    case 'imagem':
    case 'arquivo': {
      const arquivo = valor as File | undefined;
      return (
        <div className="space-y-2">
          {label}
          <div className="border border-dashed rounded-lg p-4 flex items-center gap-2 text-sm text-muted-foreground">
            <Upload className="h-4 w-4 shrink-0" />
            <label className="flex-1 cursor-pointer truncate">
              {arquivo ? arquivo.name : (campo.tipo === 'imagem' ? 'Enviar imagem ou tirar foto na hora' : 'Enviar arquivo ou tirar foto na hora')}
              <input
                type="file"
                className="hidden"
                accept={campo.tipo === 'imagem' ? 'image/*' : undefined}
                capture="environment"
                onChange={(e) => onChange(e.target.files?.[0])}
              />
            </label>
          </div>
        </div>
      );
    }
    case 'assinatura':
      return (
        <div className="space-y-2">
          {label}
          <div className="border border-dashed rounded-lg p-6 flex flex-col items-center justify-center gap-1 text-sm text-muted-foreground">
            <PenLine className="h-5 w-5" />
            Assinatura eletrônica (área de assinatura real na etapa de preenchimento)
          </div>
        </div>
      );
    default:
      return null;
  }
}

export function FormularioPreview({ formulario }: { formulario: Formulario }) {
  const [valores, setValores] = useState<Record<string, unknown>>({});
  const todosCampos = [...(formulario.campos || [])].sort((a, b) => a.ordem - b.ordem);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{formulario.titulo}</CardTitle>
          {formulario.descricao && <CardDescription>{formulario.descricao}</CardDescription>}
        </CardHeader>
        <CardContent className="space-y-5">
          {todosCampos.length === 0 ? (
            <p className="text-sm text-muted-foreground">Este formulário ainda não tem campos.</p>
          ) : (
            todosCampos.map((campo) => (
              <CampoPreview
                key={campo.id}
                campo={campo}
                valor={valores[campo.id]}
                onChange={(v) => setValores((prev) => ({ ...prev, [campo.id]: v }))}
              />
            ))
          )}
          {todosCampos.length > 0 && (
            <Button
              className="w-full"
              onClick={() => toast.info('Isso é só uma prévia — o envio de respostas ainda não está implementado.')}
            >
              Enviar
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
